import { QueryClient } from '@tanstack/react-query'
import { createRouter, stringifySearchWith } from '@tanstack/react-router'
import { setupRouterSsrQueryIntegration } from '@tanstack/react-router-ssr-query'
import { format } from 'date-fns'
import { SkeletonTheme } from 'react-loading-skeleton'
import { ErrorBoundary } from '@/components/ErrorBoundary'
import { UserLocationProvider } from '@/contexts/UserLocationProvider'
import { routeTree } from './routeTree.gen'

// Dates serialize into the URL as plain `yyyy-MM-dd` (`?dateFrom=2026-08-19`)
// instead of JSON's quoted ISO timestamp. The parse side stays default: the
// plain value fails JSON.parse, stays a string, and the routes' zod schemas
// (`z.coerce.date()`) turn it back into a Date — old quoted-ISO URLs keep
// parsing too.
const stringifySearch = stringifySearchWith((value) =>
  value instanceof Date ? format(value, 'yyyy-MM-dd') : JSON.stringify(value),
)

export function getRouter() {
  const queryClient = new QueryClient()

  const router = createRouter({
    routeTree,
    context: {
      queryClient,
    },
    stringifySearch,
    defaultPreloadStaleTime: 0,
    scrollRestoration: true,
    getScrollRestorationKey: (location) => location.pathname + location.hash,
    defaultViewTransition: true,
    Wrap: ({ children }) => (
      <ErrorBoundary>
        <UserLocationProvider>
          <SkeletonTheme enableAnimation={true}>{children}</SkeletonTheme>
        </UserLocationProvider>
      </ErrorBoundary>
    ),
  })

  // Per-request QueryClient wiring: SSR dehydrates loader-fetched queries into
  // the HTML payload and the client rehydrates them, so nothing refetches on
  // hydration. Also wraps the app in QueryClientProvider.
  setupRouterSsrQueryIntegration({ router, queryClient })

  return router
}

declare module '@tanstack/react-router' {
  interface Register {
    router: ReturnType<typeof getRouter>
  }
  // Search → detail navigation remembers where "Zpět na vyhledávání" returns.
  interface HistoryState {
    backHref?: string
  }
}
